// ==============================
// Shared Types
// ==============================
export interface iImpressionPayload {
  userId?: string;
  userClass?: string;
  source?: string;
  implementation?: string;
  event?: string;
  url?: string;
  subid?: string;
  subid2?: string;
  subid3?: string;
  subid4?: string;
  referrer?: string;
  country?: string;
  isp?: string;
  browser?: string;
  browser_version?: string;
  platform?: string;
  user_agent?: string;
}

export enum ImpressionEvent {
  PAGE_VIEW = "page_view",
  CLICK = "click",
  DOWNLOAD = "download",
}

export type StrapiResponse<T> = {
  data: T;
  message: string;
  meta?: PaginationMeta;
};
export interface PaginationMeta {
  pagination: {
    page: number;
    pageCount: number;
    pageSize: number;
    total: number;
  };
}

export type AllAppDetails = {
  app: Application;
  category?: Category;
  subcategory?: Category | null;
};

export interface MediaFile {
  documentId: string;
  id: number;
  url: string;
  name: string;
  mime: string;
  size: number;
  width?: number;
  height?: number;
  alternativeText?: string;
  caption?: string;
  formats?: any;
  createdAt?: string;
  hash?: string;
  ext?: string;
  updatedAt?: string;
}

export interface SeoMetaData {
  documentId?: string;
  id?: number;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: MediaFile | null;
}

export interface User {
  id: number;
  username: string;
  email: string;
  // Extend as needed from Users & Permissions plugin
}

// ==============================
// Application & Review Domain
// ==============================

export interface Application {
  documentId: string;
  id: number;
  title: string;
  slug: string;
  applicationType?: "legacy" | "scraped" | "developer";
  description?: string;
  ageRating?: "age3+" | "age7+" | "age12+" | "age16+" | "age18+";
  country?: string;
  excerpt?: string;
  totalDownloads?: number;
  ratingsTotalScore?: number;
  ratingsNumberOfVotes?: number;
  enabled?: boolean;
  comments?: string;
  licenseType?: "free" | "adware" | "trial" | "paid";
  legacyDevId?: number;
  legacyAppId?: number;
  downloadUrl?: string;
  legacyFileName?: string;
  legacyFileSize?: string;
  legacyVersion?: string;
  downloads30Days?: number;
  source?: string;

  parent_category?: Category | null;
  subcategory?: Category | null;
  application_versions?: ApplicationVersion[];
  platform?: Platform | null;
  publisher?: Publisher | null;

  icon?: MediaFile | null;
  cover?: MediaFile | null;
  screenshots?: MediaFile[];

  seoMetaData?: SeoMetaData[];

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface ApplicationReview {
  id: number;
  title: string;
  slug: string;
  applicationType?: "legacy" | "scraped" | "developer";
  description?: string;
  ageRating?: "age3+" | "age7+" | "age12+" | "age16+" | "age18+";
  country?: string;
  excerpt?: string;
  totalDownloads?: number;
  ratingsTotalScore?: number;
  ratingsNumberOfVotes?: number;
  enabled?: boolean;
  comments?: string;
  licenseType?: "free" | "adware" | "trial" | "paid";
  legacyDevId?: number;
  legacyAppId?: number;
  downloadUrl?: string;
  legacyFileName?: string;
  legacyFileSize?: string;
  legacyVersion?: string;
  downloads30Days?: number;
  source?: string;

  submissionStatus:
    | "pending"
    | "under_review"
    | "approved"
    | "rejected"
    | "needs_changes";
  submissionDate?: string;
  reviewDate?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  adminNotes?: string;

  parent_category?: Category | null;
  subcategory?: Category | null;
  application_versions?: ApplicationVersion[];
  platform?: Platform | null;
  publisher?: Publisher | null;
  submittedBy?: User | null;

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface Subcategory extends Category {
  parentId: number;
  parentDocumentId: string;
  parentSlug: string;
  parentDisplayName: string;
}

export interface GroupedCategories {
  [categoryType: string]: {
    categories: Category[];
    subcategories: Subcategory[];
  };
}

export interface ApplicationDownload {
  documentId: string;
  id: number;
  date?: string;
  count?: number;
  applicationId?: number;

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface ApplicationVersion {
  documentId: string;
  id: number;
  test?: string;
  externalDownloadUrl?: string;
  version?: string;
  size?: string;
  downloadFile?: string;

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

// ==============================
// Content CMS
// ==============================

export interface Article {
  documentId: string;
  id: number;
  title: string;
  slug: string;
  content?: string;
  subtitle?: string;
  isFeatured?: boolean;
  excerpt?: string;
  readingTime?: number;

  featuredImage?: MediaFile | null;
  author?: Author | null;
  category?: Category | null;
  seoMetaData?: SeoMetaData[];

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface Author {
  documentId: string;
  id: number;
  name?: string;
  bio?: string;
  slug?: string;

  avatar?: MediaFile | null;
  articles?: Article[];

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface Category {
  id: number;
  documentId: string;
  displayName?: string;
  slug?: string;
  description?: string;
  categoryType?: "application" | "article" | "game";
  seoMetaData?: SeoMetaData[];
  games?: Game[];
  applications?: Application[];
  subcategory_application?: Application[];
  parent?: Category[];
  articles?: Article[];
  legacyApplications?: boolean;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

// ==============================
// Game Center
// ==============================

export interface Game {
  documentId: string;
  id: number;
  title?: string;
  slug?: string;
  iframeUrl?: string;
  excerpt?: string;
  gamePlays?: number;

  cover?: MediaFile | null;
  squareIcon?: MediaFile | null;
  categories?: Category[];
  seoMetaData?: SeoMetaData[];

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface Platform {
  documentId: string;
  id: number;
  name?: string;
  slug?: string;
  description?: string;

  icon?: MediaFile | null;
  applications?: Application[];

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface Publisher {
  documentId: string;
  id: number;
  name?: string;
  website?: string;
  slug?: string;
  contactInfo?: string;

  applications?: Application[];

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export type ServerContextProps = {
  userID: string;
  userClass: string;
  nonce: string;
  isDeveloperZone: boolean;
  inAppMode: boolean;
  device: {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isBot: boolean;
    os: string;
    browser: string;
  };
  countryCode: string;
  platform: string;
  url: string;
  referrer: string | null;
};

export type ServerToClientProps = {
  urlProps?: any;
  data?: any;
  otherProps?: any;
};
